import { useDispatch } from 'react-redux';

import { ACCESSORIES, DealTabs } from '@makemydeal/dr-shared-types';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import type { EntityKey } from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';

export type ReadOnlyInputActionOptions = {
    tab?: DealTabs;
    type?: 'text' | 'amount';
    onClick?: () => void;
};

const { SlideOutType } = baseDealActionCreators;

const ENTITY_DEAL_TABS_MAPPING: Partial<Record<DealTabs, EntityKey>> = {
    [DealTabs.PROTECTION]: DealTabs.PROTECTION,
    [DealTabs.ACCESSORIES]: ACCESSORIES
};

const tabToSlideOutTypeMap: Record<DealTabs, baseDealActionCreators.SlideOutType | null> = {
    [DealTabs.ACCESSORIES]: SlideOutType.Accessories,
    [DealTabs.FEES]: SlideOutType.Fees,
    [DealTabs.INCENTIVES]: SlideOutType.Incentives,
    [DealTabs.LENDER]: SlideOutType.Lender,
    [DealTabs.PROTECTION]: SlideOutType.Protection,
    [DealTabs.TAXES]: SlideOutType.Taxes,
    [DealTabs.TRADE_IN]: null,
    [DealTabs.SUMMARY]: null
};

const tabToAnalyticsActionMap: Partial<Record<DealTabs, () => { type: string }>> = {
    [DealTabs.ACCESSORIES]: interactionAnalyticsActionCreators.mvAccessoriesLink,
    [DealTabs.FEES]: interactionAnalyticsActionCreators.mvFeesLink,
    [DealTabs.INCENTIVES]: interactionAnalyticsActionCreators.mvIncentivesLink,
    [DealTabs.LENDER]: interactionAnalyticsActionCreators.mvLenderLink,
    [DealTabs.PROTECTION]: interactionAnalyticsActionCreators.mvProtectionLink,
    [DealTabs.TAXES]: interactionAnalyticsActionCreators.mvTaxesLink
};

export const useReadOnlyInputAction = ({ tab, type, onClick }: ReadOnlyInputActionOptions) => {
    const dispatch = useDispatch();

    // Action Error Badge
    const entityName = ENTITY_DEAL_TABS_MAPPING[tab as DealTabs] || (tab?.toLowerCase() as EntityKey);

    const activate = () => {
        if (tab) {
            const slideOutType = tabToSlideOutTypeMap[tab];
            if (slideOutType) {
                const analyticsAction = tabToAnalyticsActionMap[tab];
                if (analyticsAction) dispatch(analyticsAction());
                dispatch(baseDealActionCreators.openSlideOut(slideOutType as baseDealActionCreators.SlideOutType));
            }
        } else if (onClick) {
            onClick();
        }
    };

    const isClickable = type === 'amount' || (type === 'text' && !!onClick);

    return { entityName, isClickable, activate };
};
